import { Fragment, ReactNode } from "react";
import { ProgramBlock, ProgramRichText } from "../data/catalogTypes";
import "./ProgramArticle.scss";

type ProgramArticleProps = {
  blocks: ProgramBlock[];
  onImageClick: (src: string) => void;
};

function richText(value: ProgramRichText): ReactNode {
  if (typeof value === "string") return value;
  return value.map((part, index) => {
    if (typeof part === "string") return <Fragment key={index}>{part}</Fragment>;
    let content: ReactNode = part.text;
    if (part.strong) content = <strong>{content}</strong>;
    if (part.emphasis) content = <em>{content}</em>;
    if (part.href) content = <a href={part.href}>{content}</a>;
    return <Fragment key={index}>{content}</Fragment>;
  });
}

export function ProgramArticle({ blocks, onImageClick }: ProgramArticleProps) {
  return (
    <div className="program-article">
      {blocks.map((block, index) => {
        switch (block.type) {
          case "paragraph": return <p key={index}>{richText(block.text)}</p>;
          case "heading": return block.level === 2 ? <h2 key={index}>{block.text}</h2> : <h3 key={index}>{block.text}</h3>;
          case "list": {
            const items = block.items.map((item, itemIndex) => <li key={itemIndex}>{richText(item)}</li>);
            return block.ordered ? <ol key={index}>{items}</ol> : <ul key={index}>{items}</ul>;
          }
          case "image": return (
            <figure key={index} className={`program-article__image program-article__image--${block.placement || "full"}`}>
              <button type="button" onClick={() => onImageClick(block.src)} aria-label={"Mărește imaginea: " + block.alt}>
                <img src={block.src} alt={block.alt} loading="lazy" />
              </button>
              {block.caption && <figcaption>{block.caption}</figcaption>}
            </figure>
          );
          case "quote": return <blockquote key={index}>{richText(block.text)}</blockquote>;
          case "callout": return <aside key={index} className="program-article__callout">{richText(block.text)}</aside>;
          case "table": return (
            <div key={index} className="program-article__table-wrap">
              <table>
                <thead><tr>{block.columns.map((column, columnIndex) => <th key={columnIndex} scope="col">{column}</th>)}</tr></thead>
                <tbody>{block.rows.map((row, rowIndex) => <tr key={rowIndex}>{row.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}</tr>)}</tbody>
              </table>
            </div>
          );
          case "divider": return <hr key={index} />;
          case "clear": return <div key={index} className="program-article__clear" />;
        }
        return null;
      })}
    </div>
  );
}
